import { useState } from "react";

/**
 * useCostBreakdownModals - Hook for managing modal state and handlers in cost breakdown
 * 
 * SUMMARY:
 * Manages state and handlers for PO, WO, and Invoice modals.
 * Provides functions to open modals with selected items and close them.
 * 
 * RESPONSIBILITIES:
 * - Manage modal visibility state (PO, WO, Invoice)
 * - Manage selected item state (PO ID, WO ID, Invoice data)
 * - Provide handlers to open/close modals
 * - Handle invoice data preparation for modal display
 * 
 * @returns {Object} Modal state and handlers
 * @returns {Object} modals - Modal visibility state
 * @returns {boolean} modals.showPOModal - PO modal visibility
 * @returns {boolean} modals.showInvoiceModal - Invoice modal visibility
 * @returns {boolean} modals.showWOModal - WO modal visibility
 * @returns {Object} selected - Selected items for modals
 * @returns {string|null} selected.selectedPOId - Selected PO ID
 * @returns {string|null} selected.selectedWOId - Selected WO ID
 * @returns {string|null} selected.selectedInvoiceNumber - Selected invoice number
 * @returns {Object} selected.selectedInvoiceData - Selected invoice data
 * @returns {Object} handlers - Modal handlers
 * @returns {Function} handlers.handleViewPO - Open PO modal with PO ID
 * @returns {Function} handlers.handleViewWO - Open WO modal with WO ID
 * @returns {Function} handlers.handleViewInvoice - Open invoice modal with invoice data
 * @returns {Function} handlers.closePOModal - Close PO modal
 * @returns {Function} handlers.closeInvoiceModal - Close invoice modal
 * @returns {Function} handlers.closeWOModal - Close WO modal
 */
export const useCostBreakdownModals = () => {
  const [showPOModal, setShowPOModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showWOModal, setShowWOModal] = useState(false);
  const [selectedPOId, setSelectedPOId] = useState(null);
  const [selectedWOId, setSelectedWOId] = useState(null);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState(null);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState({});

  const handleViewPO = (poId) => {
    if (poId && poId !== 'N/A') {
      setSelectedPOId(poId);
      setShowPOModal(true);
    }
  };

  const handleViewWO = (woId) => {
    if (woId && woId !== 'N/A') {
      setSelectedWOId(woId);
      setShowWOModal(true);
    }
  };

  const handleViewInvoice = (invoiceNumber, invoiceData = {}, invoiceId, isSubcontractor = false) => {
    if (invoiceNumber && invoiceNumber !== 'N/A') {
      setSelectedInvoiceNumber(invoiceNumber);
      setSelectedInvoiceData({
        ...invoiceData,
        invoiceId: invoiceData.invoiceId || invoiceNumber,
        vendorType: isSubcontractor ? 'sub-contractor' : 'Supplier'
      });
      setShowInvoiceModal(true);
    }
  };

  return {
    modals: {
      showPOModal,
      showInvoiceModal,
      showWOModal
    },
    selected: {
      selectedPOId,
      selectedWOId,
      selectedInvoiceNumber,
      selectedInvoiceData
    },
    handlers: {
      handleViewPO,
      handleViewWO,
      handleViewInvoice,
      closePOModal: () => setShowPOModal(false),
      closeInvoiceModal: () => setShowInvoiceModal(false),
      closeWOModal: () => setShowWOModal(false)
    }
  };
};
